import type { Currency } from "@loot-os/api-contract";
import { useCallback, useMemo } from "react";
import { useBoxDraftStore } from "./boxDraftStore";
import type { BoxEditorData } from "./boxEditor";
import {
  type BoxDraftState,
  initialBoxDraft,
  isMetaChanged,
  isTableChanged,
} from "./dropTableDraft";

export interface BoxDraft {
  /** What the editor shows: the unsaved draft, else the stored box. */
  state: BoxDraftState;
  edit: (recipe: (state: BoxDraftState) => BoxDraftState, coalesce?: string) => void;
  /** The name or image differs from the stored box: Save writes it. */
  metaChanged: boolean;
  /** The table or its pricing differs from the stored version: Publish writes it. */
  tableChanged: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  discard: () => void;
  /** The version a publish pins; the box's own when there is no draft. */
  baseVersion: number;
  /**
   * The box has a newer version than the one the draft was built on: another editor published,
   * or the five-minute processor run repriced it. Publishing would overwrite that.
   */
  stale: boolean;
  /** Keeps the draft and builds it on the box's current version from now on. */
  rebase: () => void;
}

/** The draft of one box, read from and written to the draft store. */
export function useBoxDraft(box: BoxEditorData, currency: Currency): BoxDraft {
  const entry = useBoxDraftStore((s) => s.drafts[box.id]);
  const initial = useMemo(() => initialBoxDraft(box, currency), [box, currency]);
  const state = entry?.present ?? initial;

  const edit = useCallback(
    (recipe: (state: BoxDraftState) => BoxDraftState, coalesce?: string) =>
      useBoxDraftStore
        .getState()
        .edit(box.id, { version: box.currentVersion, state: initial }, recipe, coalesce),
    [box.id, box.currentVersion, initial],
  );

  const stale = entry !== undefined && entry.baseVersion !== box.currentVersion;
  return {
    state,
    edit,
    metaChanged: isMetaChanged(state, box),
    // A stale draft was built on other rows, so compare it to nothing: it is a change.
    tableChanged: entry !== undefined && (stale || isTableChanged(state, initial)),
    canUndo: (entry?.past.length ?? 0) > 0,
    canRedo: (entry?.future.length ?? 0) > 0,
    undo: () => useBoxDraftStore.getState().undo(box.id),
    redo: () => useBoxDraftStore.getState().redo(box.id),
    discard: () => useBoxDraftStore.getState().discard(box.id),
    baseVersion: entry?.baseVersion ?? box.currentVersion,
    stale,
    rebase: () => useBoxDraftStore.getState().rebase(box.id, box.currentVersion),
  };
}

/** Whether box `boxId` has an unsaved draft in this browser: the lists' dot. */
export function useHasBoxDraft(boxId: string): boolean {
  return useBoxDraftStore((s) => s.drafts[boxId] !== undefined);
}
